// Типы для API ответов - строго соответствуют backend structure

export interface GeoLocation {
  id: string;
  name: string;
  code: string;
  flag?: string;
}

export interface User {
  login: string;
}

export interface Merchant {
  id: string;
  displayName: string;
  companyName?: string;
  contactPhone?: string;
  user: User;
  geoLocation?: GeoLocation;
}

export enum OrderStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
  EXPIRED = "EXPIRED",
}

export enum OrderType {
  DEPOSIT = "DEPOSIT",
  WITHDRAWAL = "WITHDRAWAL",
}

export interface Order {
  id: string;
  invId: string;
  type: OrderType;
  status: OrderStatus;
  amount: number;
  currency: string;
  factAmount?: number;
  bank?: string;
  cardNumber?: string;
  phoneNumber?: string;
  accountNumber?: string;
  bankIdCode?: string;
  recipientFio?: string;
  isSbp?: boolean;
  isAccountTransfer?: boolean;
  isTransGov?: boolean;
  transGovCountry?: string;
  callbackUrl?: string;
  merchantId: string;
  geoLocationId?: string;
  exchangeRate?: number;
  usdtAmount?: number;
  merchantFee?: number;
  profit?: number; // Вычисляется на backend
  commission?: number; // Вычисляется на backend
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  lastCallbackAt?: string;
  merchant?: Merchant;
}

export interface OrdersResponse {
  data: Order[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export enum AppealStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  IN_PROGRESS = "IN_PROGRESS",
  SUPPORT_REVIEW = "SUPPORT_REVIEW",
  MERCHANT_RESP_PENDING = "MERCHANT_RESP_PENDING",
  TRADER_REVIEW = "TRADER_REVIEW",
  SECONDARY_DISPUTE = "SECONDARY_DISPUTE",
}

export interface Appeal {
  id: string;
  orderId: string;
  reason?: string;
  comment?: string;
  status: AppealStatus;
  images?: string; // JSON массив
  videos?: string; // JSON массив
  documents?: string; // JSON массив
  requireClientInvoice?: boolean;
  requireTraderInvoice?: boolean;
  createdAt: string;
  updatedAt: string;
  deadline?: string;
  order?: {
    id: string;
    invId: string;
    amount: number;
    currency: string;
    status: OrderStatus;
    bank?: string;
    cardNumber?: string;
    phoneNumber?: string;
    accountNumber?: string;
    merchant?: Merchant;
  };
}

export interface AppealsResponse {
  data: Appeal[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface Profile {
  id: string;
  login: string;
  role: string;
  isActive: boolean;
  apiKey?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  merchant?: {
    id: string;
    displayName: string;
    companyName?: string;
    contactPhone?: string;
    contactEmail?: string;
    merchantType?: string;
    callbackUrl?: string;
    callbackSecret?: string;
    website?: string;
    feePercentage?: number;
    isVerified: boolean;
    isActive: boolean;
    balance: number;
    frozenBalance: number;
    createdAt: string;
    updatedAt: string;
  };
}

export interface Balance {
  balance: number;
  frozenBalance?: number;
  transitBalance?: number;
  availableBalance?: number;
  disputeBalance?: number;
  monthlyIncome?: number;
  currency: string;
  // Для разных ролей
  merchantId?: string;
  providerId?: string;
  traderId?: string;
  displayName?: string;
  name?: string;
}

export interface UploadedFile {
  type: string; // Фактически backend возвращает произвольную строку
  path: string;
  mime_type: string;
  size?: number;
  originalName?: string;
}

export interface FileUploadResponse {
  files: UploadedFile[];
}

// API параметры для запросов
export interface GetOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  bank?: string;
  amountMin?: number;
  amountMax?: number;
  usdtMin?: number;
  usdtMax?: number;
}

export interface GetAppealsParams {
  page?: number;
  limit?: number;
  status?: string;
  orderId?: string;
}

export interface CreateAppealRequest {
  orderId: string;
  reason?: string;
  comment?: string;
  images?: UploadedFile[];
  videos?: UploadedFile[];
  documents?: UploadedFile[];
}

// Типы для заявок на вывод средств
export enum WithdrawalStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export interface WithdrawalRequest {
  id: string;
  merchantId: string;
  amount: number;
  currency: string;
  walletAddress?: string;
  bankDetails?: any;
  comment?: string;
  status: WithdrawalStatus;
  adminComment?: string;
  txHash?: string;
  createdAt: string;
  updatedAt: string;
  merchant?: Merchant;
}

export interface WithdrawalsResponse {
  data: WithdrawalRequest[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GetWithdrawalsParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface CreateWithdrawalRequest {
  amount: number;
  currency?: string;
  walletAddress?: string;
  bankDetails?: {
    bankName?: string;
    accountNumber?: string;
    bankIdCode?: string;
    recipientName?: string;
    recipientInn?: string;
  };
  comment?: string;
}

// Типы для отчетов и аналитики
export interface DashboardStats {
  totalRevenue: number;
  processedVolume: number;
  processedVolumeRub: number;
  netProfit: number;
  totalOrders: number;
  completedOrders: number;
  failedOrders: number;
  cancelledOrders: number;
  totalCommissions: number;
  successRate: number;
  activeMerchants?: number;
  activeProviders?: number;
  newMerchants?: number;
  newProviders?: number;
  appealsCount?: number;
  appealPercentage?: number;
  revenueGrowth: number;
  ordersGrowth: number;
}

export interface RevenueStatsItem {
  date: string;
  revenue: number;
  orders: number;
  avgOrderAmount: number;
}

export interface RevenueStats {
  data: RevenueStatsItem[];
  summary: {
    totalRevenue: number;
    totalOrders: number;
    avgOrderAmount: number;
    growth: number;
  };
}

export interface OrderStats {
  totalOrders: number;
  completedOrders: number;
  pendingOrders: number;
  cancelledOrders: number;
  totalVolume: number;
  avgOrderAmount: number;
  successRate: number;
  totalCommission: number;
}

export interface WithdrawalStats {
  totalWithdrawals: number;
  totalAmount: number;
  pendingCount: number;
  pendingAmount: number;
  completedCount: number;
  completedAmount: number;
  rejectedCount: number;
}
